import { useState } from "react";

const vegetables = [
  { name: "Carrot", emoji: "🥕", scoreNeeded: 50, stages: ["🌱", "🌿", "🥕"], color: "bg-orange-50 border-orange-200", tagColor: "bg-orange-100 text-orange-700", desc: "Quick to grow, great for beginners!" },
  { name: "Tomato", emoji: "🍅", scoreNeeded: 120, stages: ["🌱", "🌿", "🍅"], color: "bg-red-50 border-red-200", tagColor: "bg-red-100 text-red-700", desc: "Needs more study time, worth it!" },
  { name: "Corn", emoji: "🌽", scoreNeeded: 200, stages: ["🌱", "🌿", "🌽"], color: "bg-yellow-50 border-yellow-200", tagColor: "bg-yellow-100 text-yellow-700", desc: "A tall achievement for dedicated students." },
  { name: "Watermelon", emoji: "🍉", scoreNeeded: 350, stages: ["🌱", "🌿", "🍉"], color: "bg-emerald-50 border-emerald-200", tagColor: "bg-emerald-100 text-emerald-700", desc: "Only the most committed growers reach this!" },
  { name: "Strawberry", emoji: "🍓", scoreNeeded: 500, stages: ["🌱", "🌿", "🍓"], color: "bg-rose-50 border-rose-200", tagColor: "bg-rose-100 text-rose-700", desc: "Rare and sweet — top scorer reward." },
  { name: "Pumpkin", emoji: "🎃", scoreNeeded: 800, stages: ["🌱", "🌿", "🎃"], color: "bg-amber-50 border-amber-200", tagColor: "bg-amber-100 text-amber-700", desc: "The legendary harvest. Are you ready?" },
];

const leaderboard = [
  { name: "Emily W.", score: 1240, veg: "🎃🍉🍅🥕", streak: 12 },
  { name: "Jason L.", score: 980, veg: "🍉🌽🍅🥕", streak: 9 },
  { name: "Priya M.", score: 760, veg: "🌽🍅🥕🥕", streak: 7 },
  { name: "Carlos R.", score: 540, veg: "🍅🥕🥕", streak: 5 },
  { name: "Yuki T.", score: 320, veg: "🌽🥕", streak: 4 },
];

const howItWorks = [
  { icon: "ri-book-2-line", step: "01", title: "Study & Log In", desc: "Complete lessons, attend sessions, and log in every week to earn score points.", color: "text-amber-500" },
  { icon: "ri-seedling-line", step: "02", title: "Plant Your Seeds", desc: "Spend your score points to plant vegetables on your personal virtual farm.", color: "text-emerald-600" },
  { icon: "ri-sun-line", step: "03", title: "Watch Them Grow", desc: "Keep studying! Your vegetables grow through stages as you earn more points.", color: "text-orange-500" },
  { icon: "ri-trophy-line", step: "04", title: "Harvest & Share", desc: "Harvest your crops and show off your farm on the community leaderboard.", color: "text-rose-500" },
];

export default function FarmSection() {
  const [hoveredVeg, setHoveredVeg] = useState<number | null>(null);

  return (
    <section id="farm" className="py-24 bg-gradient-to-b from-white to-emerald-50/40">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold tracking-widest uppercase mb-4">
            Farm Reward System
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-5"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Study Hard,<br />
            <span className="text-emerald-600">Grow Your Farm</span>
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">
            Every lesson completed, every week logged in earns you score points. Spend them to grow vegetables on your virtual farm — the more you study, the more your garden blooms!
          </p>
        </div>

        {/* How it works */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
          {howItWorks.map((step) => (
            <div key={step.step} className="text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white border border-gray-100 mx-auto mb-4">
                <i className={`${step.icon} text-2xl ${step.color}`}></i>
              </div>
              <span className="text-xs font-bold text-gray-300 tracking-widest">{step.step}</span>
              <h4 className="font-semibold text-gray-800 text-sm mt-1 mb-1">{step.title}</h4>
              <p className="text-gray-400 text-xs leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Vegetable Unlock Grid */}
          <div className="lg:col-span-2">
            <h3 className="font-bold text-gray-800 text-lg mb-6 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-plant-line text-emerald-600"></i>
              </div>
              Vegetables to Unlock
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {vegetables.map((veg, i) => (
                <div
                  key={veg.name}
                  className={`rounded-2xl p-5 border-2 cursor-pointer transition-all duration-200 ${veg.color} ${hoveredVeg === i ? "scale-105" : ""}`}
                  onMouseEnter={() => setHoveredVeg(i)}
                  onMouseLeave={() => setHoveredVeg(null)}
                >
                  <div className="text-4xl mb-3 text-center">
                    {hoveredVeg === i ? veg.stages[2] : veg.stages[0]}
                  </div>
                  <p className="font-bold text-gray-800 text-sm text-center mb-1">{veg.name}</p>
                  <div className="flex justify-center mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${veg.tagColor}`}>
                      {veg.scoreNeeded} pts
                    </span>
                  </div>
                  <p className="text-gray-400 text-xs text-center leading-relaxed">{veg.desc}</p>
                  {/* Growth stages */}
                  <div className="flex justify-center gap-1 mt-3">
                    {veg.stages.map((stage, si) => (
                      <span key={si} className="text-base">{stage}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Sample farm plot */}
            <div className="mt-8 bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-800 text-sm">Sample Student Farm — Emily W.</h4>
                <span className="text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full">1,240 pts total</span>
              </div>
              <div className="grid grid-cols-8 gap-2">
                {["🥕","🥕","🍅","🥕","🌽","🍅","🍉","🎃","🥕","🍅","🌽","🥕","🍓","🍅","🥕","🌽"].map((v, i) => (
                  <div key={i} className="w-full aspect-square flex items-center justify-center rounded-lg bg-emerald-50 text-xl border border-emerald-100">
                    {v}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Leaderboard */}
          <div>
            <h3 className="font-bold text-gray-800 text-lg mb-6 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-bar-chart-line text-amber-500"></i>
              </div>
              Top Farmers This Month
            </h3>
            <div className="flex flex-col gap-3">
              {leaderboard.map((user, i) => (
                <div key={user.name} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
                  <div className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full font-bold text-sm ${
                    i === 0 ? "bg-amber-400 text-white" :
                    i === 1 ? "bg-gray-300 text-white" :
                    i === 2 ? "bg-orange-300 text-white" :
                    "bg-gray-100 text-gray-500"
                  }`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 text-sm">{user.name}</p>
                    <p className="text-xs text-gray-400">{user.veg} · 🔥 {user.streak}wk streak</p>
                  </div>
                  <span className="text-sm font-bold text-emerald-600 flex-shrink-0">{user.score.toLocaleString()}</span>
                </div>
              ))}
            </div>

            {/* Score breakdown */}
            <div className="mt-6 bg-emerald-50 rounded-2xl p-5">
              <h4 className="font-semibold text-gray-800 text-sm mb-4">How to Earn Points</h4>
              <ul className="flex flex-col gap-2.5">
                {[
                  { action: "Weekly login", pts: "+10 pts", icon: "ri-calendar-check-line" },
                  { action: "Complete a lesson", pts: "+20 pts", icon: "ri-book-2-line" },
                  { action: "Attend live session", pts: "+30 pts", icon: "ri-video-line" },
                  { action: "Submit an essay", pts: "+50 pts", icon: "ri-file-text-line" },
                  { action: "Refer a friend", pts: "+100 pts", icon: "ri-user-add-line" },
                  { action: "Login streak (4wk)", pts: "+80 pts bonus", icon: "ri-fire-line" },
                ].map((item) => (
                  <li key={item.action} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${item.icon} text-emerald-600 text-sm`}></i>
                      </div>
                      <span className="text-gray-600 text-xs">{item.action}</span>
                    </div>
                    <span className="text-emerald-700 font-bold text-xs">{item.pts}</span>
                  </li>
                ))}
              </ul>
            </div>

            <a
              href="/farm"
              className="mt-5 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-seedling-line"></i>
              </div>
              Start Growing Your Farm
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
